<div class="wizard-step-content">
  <div class="d-flex justify-content-between align-items-start mb-4">
    <div>
      <h3 class="mb-2">
        <i class="fa fa-box-open text-primary me-2"></i>
        {{ 'eventWizard.packages.title' | translate }}
      </h3>
      <p class="text-muted mb-0">
        {{ 'eventWizard.packages.subtitle' | translate }}
      </p>
    </div>
    <app-button
      [variant]="'primary'"
      [label]="'eventWizard.packages.addPackage' | translate"
      [iconClass]="'fa fa-plus'"
      (clicked)="openAddModal()"
    />
  </div>

  <!-- Page Info -->
  <div class="alert alert-light-primary d-flex align-items-start gap-3 mb-4 border border-primary border-opacity-25 rounded-3 p-4">
    <i class="fa fa-circle-info fs-2 text-primary mt-1"></i>
    <div>
      <h5 class="fw-bold mb-2">{{ 'manageEvent.trainingPackages.infoTitle' | translate }}</h5>
      <p class="text-gray-700 mb-2">{{ 'manageEvent.trainingPackages.infoDescription' | translate }}</p>
      <ul class="text-gray-600 fs-7 mb-0 ps-4">
        <li>{{ 'manageEvent.trainingPackages.infoStep1' | translate }}</li>
        <li>{{ 'manageEvent.trainingPackages.infoStep2' | translate }}</li>
        <li>{{ 'manageEvent.trainingPackages.infoStep3' | translate }}</li>
      </ul>
    </div>
  </div>

  @if (loading) {
    <div class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'common.loading' | translate }}</span>
      </div>
    </div>
  } @else if (packages.length === 0) {
    <div class="empty-state">
      <i class="fa fa-box-open"></i>
      <h5>{{ 'eventWizard.packages.noPackagesYet' | translate }}</h5>
      <p>{{ 'eventWizard.packages.createFirstPackage' | translate }}</p>
      <app-button
        [variant]="'primary'"
        [label]="'eventWizard.packages.createPackage' | translate"
        [iconClass]="'fa fa-plus'"
        (clicked)="openAddModal()"
      />
    </div>
  } @else {
    <div class="packages-grid">
      @for (pkg of packages; track pkg.package_id) {
        <div class="package-card">
          <div class="package-header">
            <h5 class="package-name">{{ pkg.package_name }}</h5>
            <div class="package-actions">
              <button class="btn btn-sm btn-light" (click)="openEditModal(pkg)">
                <i class="fa fa-edit"></i>
              </button>
              <button class="btn btn-sm btn-light text-danger" (click)="deletePackage(pkg)">
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>

          @if (pkg.package_description) {
            <p class="package-description">{{ pkg.package_description }}</p>
          }

          <div class="package-details">
            <div class="detail-item">
              <i class="fa fa-calendar-check"></i>
              <span>{{ 'eventWizard.packages.sessionsCount' | translate:{ count: pkg.session_count } }}</span>
            </div>
            <div class="detail-item">
              <i class="fa fa-users"></i>
              <span>{{ getSessionTypeLabel(pkg.session_type) }}</span>
            </div>
            @if (pkg.max_registrations) {
              <div class="detail-item">
                <i class="fa fa-user-check"></i>
                <span>{{ 'eventWizard.packages.maxRegistrations' | translate:{ count: pkg.max_registrations } }}</span>
              </div>
            }
            @if (pkg.package_days && pkg.package_days.length > 0) {
              <div class="detail-item">
                <i class="fa fa-calendar-alt"></i>
                <span class="day-breakdown">
                  @for (pkgDay of pkg.package_days; track pkgDay.package_day_id; let last = $last) {
                    {{ pkgDay.session_count }} on {{ formatDate(pkgDay.training_date) }}{{ last ? '' : ', ' }}
                  }
                </span>
              </div>
            }
          </div>

          <div class="package-price">
            <span class="price-amount">{{ formatPrice(pkg.price_cents, event?.default_currency || pkg.currency) }}</span>
            @if (pkg.vat_rate > 0) {
              <span class="price-vat">+ {{ pkg.vat_rate }}% {{ 'eventWizard.packages.vat' | translate }}</span>
            }
          </div>
        </div>
      }
    </div>
  }
</div>

<!-- Add/Edit Package Modal -->
@if (showAddModal) {
  <div class="modal-backdrop" (click)="closeModal()"></div>
  <div class="modal-container">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">{{ editingPackage ? ('eventWizard.packages.editPackage' | translate) : ('eventWizard.packages.addPackage' | translate) }}</h5>
        <button type="button" class="btn-close" (click)="closeModal()"></button>
      </div>
      <div class="modal-body">
        <div class="mb-3">
          <app-input-component
            [label]="('eventWizard.packages.packageName' | translate) + ' *'"
            [type]="'text'"
            [placeholder]="'eventWizard.packages.packageNamePlaceholder' | translate"
            [value]="packageName"
            (valueChange)="packageName = $event"
          />
        </div>

        <div class="mb-3">
          <label class="form-label">{{ 'eventWizard.packages.description' | translate }}</label>
          <textarea
            class="form-control"
            rows="2"
            [placeholder]="'eventWizard.packages.descriptionPlaceholder' | translate"
            [(ngModel)]="packageDescription"
          ></textarea>
        </div>

        <div class="row g-3 mb-3">
          <div class="col-6">
            <app-input-component
              [label]="('eventWizard.packages.numberOfSessions' | translate) + ' *'"
              [type]="'number'"
              [placeholder]="'1'"
              [value]="sessionCount.toString()"
              (valueChange)="sessionCount = +$event"
            />
          </div>
          <div class="col-6">
            <app-selectorini
              [options]="sessionTypes"
              [displayKey]="'label'"
              [valueKey]="'value'"
              [label]="('eventWizard.packages.sessionType' | translate) + ' *'"
              [placeholder]="'eventWizard.packages.selectType' | translate"
              [floating]="true"
              [value]="sessionType"
              (valueChange)="sessionType = $event"
            />
          </div>
        </div>

        <!-- Free Checkbox -->
        <div class="mb-3">
          <div class="form-check">
            <input
              class="form-check-input"
              type="checkbox"
              id="isFreePackage"
              [checked]="isFree"
              (change)="toggleFree($any($event.target).checked)"
            >
            <label class="form-check-label" for="isFreePackage">
              {{ 'eventWizard.packages.freePackage' | translate }}
            </label>
            <div class="form-text text-muted">{{ 'eventWizard.packages.freePackageHint' | translate }}</div>
          </div>
        </div>

        @if (!isFree) {
          <div class="row g-3 mb-3">
            <div class="col-4">
              <app-input-component
                [label]="('eventWizard.packages.price' | translate) + ' *'"
                [type]="'text'"
                [placeholder]="'0.00'"
                [value]="priceDisplay"
                (valueChange)="onPriceChange($event)"
              />
              @if (priceCents > 0) {
                <div class="text-muted small mt-1">
                  {{ 'common.netAmountHint' | translate:{ amount: calculateNetAmount(priceCents), currency: currency || 'NOK' } }}
                </div>
              }
            </div>
            <div class="col-4">
              <app-selectorini
                [options]="currencyOptions"
                [displayKey]="'name'"
                [valueKey]="'code'"
                [label]="'eventWizard.packages.currency' | translate"
                [floating]="true"
                [value]="currency"
                [disabled]="true"
              />
            </div>
            <div class="col-4">
              <app-selectorini
                [options]="vatOptions"
                [displayKey]="'label'"
                [valueKey]="'value'"
                [label]="'eventWizard.packages.vatRate' | translate"
                [placeholder]="'eventWizard.packages.selectVat' | translate"
                [floating]="true"
                [value]="vatRate"
                (valueChange)="vatRate = $event"
              />
            </div>
          </div>
        }

        <div class="mb-3">
          <app-input-component
            [label]="'eventWizard.packages.maxRegistrationsOptional' | translate"
            [type]="'number'"
            [placeholder]="'eventWizard.packages.leaveEmptyUnlimited' | translate"
            [value]="maxRegistrations?.toString() || ''"
            (valueChange)="maxRegistrations = $event ? +$event : null"
          />
        </div>

        <!-- Day Distribution Section -->
        @if (getDaysWithCapacity().length > 0) {
          <div class="day-distribution-section">
            <div class="section-header mb-2">
              <i class="fa fa-calendar-alt text-primary me-2"></i>
              <span class="fw-semibold">{{ 'eventWizard.packages.sessionDistribution' | translate }}</span>
              <span class="text-muted ms-2">({{ 'eventWizard.packages.optional' | translate }})</span>
            </div>
            <p class="text-muted small mb-3">
              {{ 'eventWizard.packages.distributionHelp' | translate }}
            </p>

            <div class="day-distribution-grid">
              @for (day of getDaysWithCapacity(); track day.training_date) {
                <div class="day-distribution-item">
                  <label class="day-label">{{ day.dayLabel }}</label>
                  <input
                    type="number"
                    class="form-control form-control-sm"
                    min="0"
                    [max]="sessionCount"
                    [(ngModel)]="day.session_count"
                    placeholder="0"
                  />
                </div>
              }
            </div>

            @if (getTotalDaySessions() > 0) {
              <div class="distribution-summary mt-2" [class.text-danger]="getTotalDaySessions() !== sessionCount">
                <i class="fa" [class.fa-check-circle]="getTotalDaySessions() === sessionCount" [class.fa-exclamation-circle]="getTotalDaySessions() !== sessionCount"></i>
                {{ 'eventWizard.packages.totalSessions' | translate:{ current: getTotalDaySessions(), total: sessionCount } }}
                @if (getTotalDaySessions() !== sessionCount) {
                  <span class="ms-1">({{ 'eventWizard.packages.mustMatch' | translate }})</span>
                }
              </div>
            }
          </div>
        } @else {
          <div class="alert alert-info small mb-0">
            <i class="fa fa-info-circle me-2"></i>
            {{ 'eventWizard.packages.setupCapacityFirst' | translate }}
          </div>
        }
      </div>
      <div class="modal-footer">
        <app-button
          [variant]="'ghost'"
          [label]="'common.cancel' | translate"
          (clicked)="closeModal()"
        />
        <app-button
          [variant]="'primary'"
          [label]="editingPackage ? ('common.saveChanges' | translate) : ('eventWizard.packages.addPackage' | translate)"
          [disabled]="!packageName || sessionCount < 1"
          (clicked)="savePackage()"
        />
      </div>
    </div>
  </div>
}
